/**
 * Web entry point for vizi-localization
 *
 * On React Native, Metro should load index.native.ts instead.
 * This file is a fallback that works if Metro loads the wrong file.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {
  resources,
  defaultLanguage,
  defaultNamespace,
  supportedLanguages,
} from './locales';

export type { SupportedLanguage, Namespace } from './types';
export {
  resources,
  supportedLanguages,
  defaultLanguage,
  defaultNamespace,
  languageNames,
} from './locales';

// Re-export from react-i18next for convenience
export { useTranslation, Trans } from 'react-i18next';

/**
 * Initialize i18next with React bindings and language detection.
 * This function should be called once at app startup (web only).
 */
export function initI18n() {
  if (i18n.isInitialized) {
    return i18n;
  }

  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: defaultLanguage,
      supportedLngs: supportedLanguages,
      defaultNS: defaultNamespace,
      ns: ['common', 'admin', 'native', 'errors'],

      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: 'vizi-language',
        caches: ['localStorage'],
      },

      interpolation: {
        escapeValue: false,
      },

      debug: false,

      react: {
        useSuspense: false,
      },
    });

  return i18n;
}

/**
 * Native-specific initialization function.
 * On web/fallback, this calls initI18n() and returns the instance.
 * On React Native, Metro should resolve to index.native.ts which has async device locale detection.
 */
export async function initI18nNative(): Promise<typeof i18n> {
  initI18n();
  return i18n;
}

/**
 * Clears stored language preference (stub on web).
 */
export async function clearStoredLanguage(): Promise<void> {
  // No-op on web
}

/**
 * Gets the device's detected locale (stub on web).
 */
export function getDeviceLocale(): 'en' | 'es' {
  return (i18n.language || 'en') as 'en' | 'es';
}

export { i18n };
